import * as React from "react";
import axios, {AxiosError} from "axios";
import {Row} from "react-bootstrap";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {Spinner} from "../common/ui/spinner/Spinner";
import {DimensionProps, Dimensions} from "../common/ui/dimensions/Dimensions";
import {fullViewPortDimensions} from "../common/ui/table/ControlRoomTable";
import {Redirect} from "react-router-dom";
import {ErrorDisplay} from "../common/ui/errordisplay/ErrorDisplay";

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {year: 'numeric', month: 'long', day: 'numeric'};
const ABOUT_PAGE_MESSAGES = defineMessages({
  ERROR: {
    id: "studio.about.error-detail",
    defaultMessage: "Unable to load details. Please contact Support.",
  },
  LICENSE_EXPIRY: {
    id: "studio.about.license-expiry",
    defaultMessage: "Expiry Date",
  },
  LICENSE_DAYS: {
    id: "studio.about.license-days",
    defaultMessage: "Days",
  },
  LICENSE_NOT_AVAILABLE: {
    id: "studio.about.license-not-available",
    defaultMessage: "License is not available",
  }
});

interface AboutPageState {
  loading: boolean;
  name: string;
  version: string;
  copyright: string;
  acknowledgements: string;
  license: string;
  errorMessage: string;
  error: AxiosError;
}

export class AboutPageComponent extends React.Component<InjectedIntlProps & DimensionProps, AboutPageState> {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      name: "",
      version: "",
      copyright: "",
      acknowledgements: "",
      license: "",
      errorMessage: "",
      error: null,
    };
  }

  componentDidMount() {
    const {intl} = this.props;
    let license = "";
    let acknowledgements = "";

    Promise.allSettled([
      axios.get('../actuators/info'),
      axios.get('../actuators/acknowledgements'),
      axios.get('../actuators/health/license')
    ]).then((response) => {
      let error = null;
      const errorMessage = (response[0].status === 'fulfilled' &&
                            response[1].status === 'fulfilled') ? "" : intl.formatMessage(ABOUT_PAGE_MESSAGES.ERROR);
      if (response[0].status === 'fulfilled') {
        const result = response[0] as PromiseFulfilledResult<any>;
        this.setInfo(result);
      } else if (response[0].reason.response.status === 401) {
        error = response[0].reason;
      }
      if (response[1].status === 'fulfilled') {
        const result = response[1] as PromiseFulfilledResult<any>;
        acknowledgements = result.value.data;
      } else if (response[1].reason.response.status === 401) {
        error = response[1].reason;
      }
      if (response[2].status === 'fulfilled') {
        const result = response[2] as PromiseFulfilledResult<any>;
        license = this.getLicenseString(result);
      } else {
        license = `ERROR${intl.formatMessage(ABOUT_PAGE_MESSAGES.LICENSE_NOT_AVAILABLE)}`;
        if (response[2].reason.response.status === 401) {
          error = response[2].reason;
        }
      }

      this.setState({
        acknowledgements,
        license,
        loading: false,
        errorMessage,
        error,
      });
    })
        .catch((error) => {
          this.setState({error})
        });
  }

  render() {
    const {loading, errorMessage, error} = this.state;

    if (error) {
      if (error?.response?.status === 401) {
        return <Redirect to="/login"/>;
      }
      return <ErrorDisplay error={error}/>;
    }
    if (loading) {
      return this.renderLoading();
    }
    if (errorMessage !== "") {
      return this.errorPage();
    } else {
      return this.aboutPage();
    }
  }

  private setInfo(result) {
    let name = "";
    let version = "";
    let copyright = "";

    if (result.value.data.application) {
      name = result.value.data.application.name ? result.value.data.application.name : "";
      version = result.value.data.application?.installer?.version ? result.value.data.application.installer.version
                                                                  : "";
      copyright = result.value.data.application.copyright ? result.value.data.application.copyright : "";
    }

    this.setState({name, version, copyright});
  }

  private getLicenseString = (result) => {
    const {intl} = this.props;
    let license = "";

    if (result.value.status === 200 && result.value.data.status &&
        result.value.data.status === "UP" && result.value.data.details) {
      if (result.value.data.details.expiryDays &&
          result.value.data.details.featureDescription &&
          result.value.data.details.licenseNotice) {
        const expiryDate = new Date(
            new Date().setDate(new Date().getDate() + result.value.data.details.expiryDays));
        license = `${result.value.data.details.featureDescription} (${result.value.data.details.feature})\n` +
                  `${result.value.data.details.licenseNotice}\n` +
                  `${intl.formatMessage(ABOUT_PAGE_MESSAGES.LICENSE_EXPIRY)}: ${expiryDate.toLocaleDateString(
                      undefined, DATE_FORMAT_OPTIONS)} ` +
                  `(${result.value.data.details.expiryDays} ${intl.formatMessage(
                      ABOUT_PAGE_MESSAGES.LICENSE_DAYS)})`;
      }
    } else {
      license = `ERROR${intl.formatMessage(ABOUT_PAGE_MESSAGES.LICENSE_NOT_AVAILABLE)}`;
    }
    return license;
  }

  private aboutPage = () => {
    const {name, version, copyright, acknowledgements} = this.state;
    const {containerHeight} = this.props;
    const acknowledgementsHeight = containerHeight - 250;

    return (
        <div className="aboutPage">
          <h2><FormattedMessage id="studio.about.title" defaultMessage="About ERDAS APOLLO Studio"/></h2>
          <hr/>
          <table>
            <tbody>
            <tr>
              <td className="title">
                <strong>
                  <FormattedMessage id="studio.about.name" defaultMessage="Name: "/>
                </strong>
              </td>
              <td className="detail">{name}</td>
            </tr>
            <tr>
              <td className="title">
                <strong>
                  <FormattedMessage id="studio.about.version" defaultMessage="Version: "/>
                </strong>
              </td>
              <td className="detail">{version}</td>
            </tr>
            <tr>
              <td className="title">
                <strong>
                  <FormattedMessage id="studio.about.copyright" defaultMessage="Copyright: "/>
                </strong>
              </td>
              <td className="detail">{copyright}</td>
            </tr>
            <tr>
              <td className="titleMultiLine">
                <div className="titleMultiLine">
                  <strong>
                    <FormattedMessage id="studio.about.license" defaultMessage="License: "/>
                  </strong>
                </div>
              </td>
              <td className="detailMultiLine">
                <div className="license" style={{whiteSpace: "pre-wrap"}}>
                  {this.getLicense()}
                </div>
              </td>
            </tr>
            <tr>
              <td className="titleMultiLine">
                <div className="titleMultiLine">
                  <strong>
                    <FormattedMessage id="studio.about.acknowledgements"
                                      defaultMessage="Acknowledgements: "/>
                  </strong>
                </div>
              </td>
              <td className="detailMultiLine">
                <div className="acknowledgements" style={{
                  maxHeight: acknowledgementsHeight,
                  overflowY: "scroll",
                  whiteSpace: "pre-wrap",
                  direction: "rtl"
                }}>
                  <div className="acknowledgementsInside" style={{
                    direction: "ltr",
                    marginLeft: "15px"
                  }}>
                    {acknowledgements}
                  </div>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
    );
  }

  private getLicense = () => {
    const {license} = this.state;

    if (license.startsWith("ERROR")) {
      return <div className="licenseError">{license.substring(5)}</div>;
    } else {
      return <div>{license}</div>;
    }
  }

  private errorPage = () => {
    const {errorMessage} = this.state;
    return (
        <div>
          <h2><FormattedMessage id="studio.about.title" defaultMessage="About ERDAS APOLLO Studio"/></h2>
          <hr/>
          <div>{errorMessage}</div>
        </div>
    );
  }

  private renderLoading = () => {
    return (
        <Row style={{textAlign: "center"}}>
          <h2>
            <FormattedMessage id="studio.settings.general.loading"
                              defaultMessage="Loading..."/>
          </h2>
          <Spinner bigStyle/>
        </Row>
    );
  }
}

export const AboutPage = injectIntl(Dimensions(fullViewPortDimensions)(AboutPageComponent));